import React, { Suspense } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { LinkEmbed } from '@atlassian/smart-knowledge-cards/link-embed';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';
import { UserInlinePreviewList } from '@atlassian/user-inline-preview';

import { ProjectHighlightsElement } from '../sub-agents/components/project-highlights/project-highlights';
import { RelatedPeopleElement } from '../sub-agents/components/related-people/related-people';
import type {
	ActionElementType,
	PeopleEntityElementType,
	PeopleEntityListElementType,
	SmartLinkElementType,
	SmartLinkListElementType,
} from '../types';

import { CarouselElement } from './carousel-element';
import { MarkdownElement } from './markdown-element';
import { PeopleEntityInlineRelay } from './people-entity/people-entity-inline-relay';
import { PeopleEntityProfileRelay } from './people-entity/people-entity-profile-relay';
import { SmartLinkElement } from './smartlink-element';
import type { ElementRenderer, ElementTag } from './types';

const styles = cssMap({
	linkListWrapper: {
		marginTop: token('space.100'),
		width: '100%',
	},
	linkEmbedWrapper: {
		marginTop: token('space.100'),
		width: '100%',
	},
	peopleEntityWrapper: {
		marginTop: token('space.100'),
		width: '100%',
	},
});

/**
 * Adapter for smart link elements
 * Ensures all smart links are rendered as standalone cards.
 * When smartLinkRenderer is 'embed', renders a hero embed card instead.
 */
const SmartLinkElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
	smartLinkRenderer,
}) => {
	const url = (component as SmartLinkElementType).attributes?.url;
	if (smartLinkRenderer === 'embed' && url) {
		return (
			<Box xcss={styles.linkEmbedWrapper}>
				<LinkEmbed url={url} />
			</Box>
		);
	}

	return (
		<SmartLinkElement
			component={component as SmartLinkElementType}
			variant={variant}
			isStreaming={isStreaming}
			sectionKey={sectionKey}
			containerType={containerType}
			indexInContainer={indexInContainer}
		/>
	);
};

/**
 * Adapter for action card elements
 * Converts action card elements into smart link elements with validation
 */
const ActionCardElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
}) => {
	const actionCard = component as ActionElementType;

	// Validate component has a url and convert to smart link
	if (!actionCard.attributes?.url) {
		return null;
	}

	const smartLinkComponent: SmartLinkElementType = {
		tag: 'smart-link',
		attributes: {
			url: actionCard.attributes.url,
			title: actionCard.attributes?.title,
			type: 'action',
			pos: actionCard.attributes?.pos,
		},
	};

	return (
		<SmartLinkElement
			component={smartLinkComponent}
			variant={variant}
			isStreaming={isStreaming}
			sectionKey={sectionKey}
			containerType={containerType}
			indexInContainer={indexInContainer}
		/>
	);
};

/**
 * Adapter for related people sub-agent elements
 * Extracts related people data from the sub-agent data map
 */
const RelatedPeopleElementAdapter: ElementRenderer = ({ subAgentDataMap, isStreaming }) => (
	<RelatedPeopleElement data={subAgentDataMap.relatedPeople} isStreaming={isStreaming} />
);

/**
 * Adapter for project highlights sub-agent elements
 * Extracts project highlights data from the sub-agent data map
 */
const ProjectHighlightsElementAdapter: ElementRenderer = ({ subAgentDataMap, isStreaming }) => (
	<ProjectHighlightsElement data={subAgentDataMap.projectHighlights} isStreaming={isStreaming} />
);

/**
 * Adapter for smart link list elements
 * Renders a vertical stack of smart link cards from item children
 */

const SmartLinkListElementAdapter: ElementRenderer = ({ component }) => {
	const listComponent = component as SmartLinkListElementType;
	const items = Array.isArray(listComponent.children) ? listComponent.children : [];
	const linkListUrls = items
		.map((item) => {
			if (typeof item === 'string') {
				return null;
			}
			const attrs = item.attributes as Record<string, string | undefined> | undefined;
			return attrs?.['url'];
		})
		.filter((item): item is string => !!item);

	return (
		<Box xcss={styles.linkListWrapper}>
			<LinkList urls={linkListUrls} headerVisible />
		</Box>
	);
};

/**
 * Single people entity — Relay profile preview (`UserProfilePreviewRelay`).
 */
const PeopleEntityElementAdapter: ElementRenderer = ({ component, ...props }) => {
	const accountId = (component as PeopleEntityElementType).attributes?.accountId;
	if (!accountId) {
		return null;
	}

	return (
		<Box xcss={styles.peopleEntityWrapper}>
			<Suspense fallback={null}>
				<PeopleEntityProfileRelay accountId={accountId} {...props} />
			</Suspense>
		</Box>
	);
};

/**
 * List of people entities — `UserInlinePreviewList` of Relay-backed `UserInlinePreview` rows.
 */
const PeopleEntityListElementAdapter: ElementRenderer = ({ component, source, ...props }) => {
	const listComponent = component as PeopleEntityListElementType;
	const items = Array.isArray(listComponent.children) ? listComponent.children : [];
	const rows = items
		.map((item, index) => {
			if (typeof item === 'string' || item.tag !== 'people-entity') {
				return null;
			}
			const accountId = (item as PeopleEntityElementType).attributes?.accountId;
			if (!accountId) {
				return null;
			}
			return (
				<Suspense key={`${accountId}-${String(index)}`} fallback={null}>
					<PeopleEntityInlineRelay accountId={accountId} {...props} />
				</Suspense>
			);
		})
		.filter((node): node is React.JSX.Element => node != null);

	const inSAINFeature = source === 'luminaSAIN';

	if (!rows.length) {
		return null;
	}

	return (
		<Box xcss={styles.peopleEntityWrapper}>
			<UserInlinePreviewList
				testId="lumina-people-entity-list"
				initialVisibleCount={inSAINFeature ? 8 : 4}
				layout={inSAINFeature ? 'grid' : 'stack'}
			>
				{rows}
			</UserInlinePreviewList>
		</Box>
	);
};

/**
 * Centralized map of element renderers
 * Maps element tags to their corresponding component references
 *
 * Components with direct references (MarkdownElement, CarouselElement) require no adaptation.
 * Components with adapters handle prop mapping/data transformation.
 */
export const elementRenderers: Record<ElementTag, ElementRenderer> = {
	'action-card': ActionCardElementAdapter,
	carousel: CarouselElement as ElementRenderer,
	md: MarkdownElement as ElementRenderer,
	'people-entity': PeopleEntityElementAdapter,
	'people-entity-list': PeopleEntityListElementAdapter,
	'project-highlights': ProjectHighlightsElementAdapter,
	'related-people': RelatedPeopleElementAdapter,
	'smart-link': SmartLinkElementAdapter,
	'smart-link-list': SmartLinkListElementAdapter,
};
